import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Plus, Pencil, Trash2, RefreshCw, Save, X, ToggleLeft, ToggleRight,
  Calendar, CalendarDays, Target, Trophy, Dice5, Gamepad2, Wallet, Users, Flame, DollarSign, Gift, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ICON_OPTIONS = [
  { name: 'Target',     Comp: Target },
  { name: 'Calendar',   Comp: Calendar },
  { name: 'CalendarDays', Comp: CalendarDays },
  { name: 'Trophy',     Comp: Trophy },
  { name: 'Dice5',      Comp: Dice5 },
  { name: 'Gamepad2',   Comp: Gamepad2 },
  { name: 'Wallet',     Comp: Wallet },
  { name: 'Users',      Comp: Users },
  { name: 'Flame',      Comp: Flame },
  { name: 'DollarSign', Comp: DollarSign },
  { name: 'Gift',       Comp: Gift },
  { name: 'Crown',      Comp: Crown },
];

const COLOR_OPTIONS = ['#22C55E', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#FCD34D'];

const EMPTY = {
  kind: 'daily',
  title: '',
  description: '',
  reward: '',
  reward_value: 0,
  icon: 'Target',
  accent: '#22C55E',
  active: true,
  sort_order: 0,
};

export default function AdminTasksPage() {
  const [activeTab, setActiveTab] = useState('daily');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/tasks-v2?kind=${activeTab}`);
      setTasks(res.data?.tasks || []);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const seedDefaults = async () => {
    try {
      const res = await api.post('/api/admin/tasks-v2/seed-defaults');
      toast.success(`Seeded ${res.data?.created || 0} default tasks`);
      load();
    } catch {
      toast.error('Seed failed');
    }
  };

  const startNew = () => {
    setEditing('new');
    setForm({ ...EMPTY, kind: activeTab, sort_order: tasks.length + 1 });
  };

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      kind: t.kind, title: t.title, description: t.description, reward: t.reward,
      reward_value: t.reward_value, icon: t.icon, accent: t.accent,
      active: t.active, sort_order: t.sort_order,
    });
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/admin/tasks-v2', form);
        toast.success('Task created');
      } else {
        await api.put(`/api/admin/tasks-v2/${editing.id}`, form);
        toast.success('Task updated');
      }
      await load();
      cancelEdit();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/api/admin/tasks-v2/${t.id}`, { ...t, active: !t.active });
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/api/admin/tasks-v2/${t.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-tasks">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Tasks</h2>
          <p className="text-sm text-gray-400">Configure daily and weekly tasks shown on the player Explore &amp; Earn pages.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-white/10" data-testid="at-reload">
            <RefreshCw className="w-4 h-4 mr-1" /> Reload
          </Button>
          {tasks.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} className="border-emerald-500/40 text-emerald-300" data-testid="at-seed">
              Seed Defaults
            </Button>
          )}
          <Button onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="at-new">
            <Plus className="w-4 h-4 mr-1" /> New {activeTab === 'daily' ? 'Daily' : 'Weekly'} Task
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: '#0B0E14', border: '1px solid rgba(255,255,255,0.06)' }}>
        <TabBtn active={activeTab === 'daily'} onClick={() => { cancelEdit(); setActiveTab('daily'); }} icon={Calendar} label="Daily Tasks" testid="at-tab-daily" />
        <TabBtn active={activeTab === 'weekly'} onClick={() => { cancelEdit(); setActiveTab('weekly'); }} icon={CalendarDays} label="Weekly Tasks" testid="at-tab-weekly" />
      </div>

      {/* Editor */}
      {editing && (
        <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="at-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">
              {editing === 'new' ? 'New' : 'Edit'} {form.kind === 'daily' ? 'Daily' : 'Weekly'} Task
            </h3>
            <button onClick={cancelEdit} className="p-2 rounded-lg hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Kind">
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full h-10 rounded-md px-3 bg-[#0B0E14] border border-white/10 text-sm"
                data-testid="at-field-kind"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </Field>
            <Field label="Sort order">
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || '0', 10) })}
                className="bg-[#0B0E14] border-white/10" data-testid="at-field-order" />
            </Field>
            <Field label="Title" full>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Daily Login" className="bg-[#0B0E14] border-white/10" data-testid="at-field-title" />
            </Field>
            <Field label="Description" full>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What the player needs to do" className="bg-[#0B0E14] border-white/10" data-testid="at-field-desc" />
            </Field>
            <Field label="Reward (headline)">
              <Input value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })}
                placeholder="e.g. +50 FP" className="bg-[#0B0E14] border-white/10" data-testid="at-field-reward" />
            </Field>
            <Field label="Reward value (numeric)">
              <Input type="number" value={form.reward_value}
                onChange={(e) => setForm({ ...form, reward_value: parseFloat(e.target.value || '0') })}
                className="bg-[#0B0E14] border-white/10" data-testid="at-field-rewardv" />
            </Field>
            <Field label="Icon" full>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ name, Comp }) => {
                  const sel = form.icon === name;
                  return (
                    <button key={name} type="button" onClick={() => setForm({ ...form, icon: name })}
                      className="px-3 py-2 rounded-lg flex items-center gap-1.5 transition"
                      style={{
                        background: sel ? `${form.accent}33` : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${sel ? form.accent : 'rgba(255,255,255,0.08)'}`,
                        color: sel ? form.accent : '#fff',
                      }}
                      data-testid={`at-icon-${name}`}>
                      <Comp className="w-4 h-4" />
                      <span className="text-xs">{name}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Accent color" full>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => {
                  const sel = form.accent === c;
                  return (
                    <button key={c} type="button" onClick={() => setForm({ ...form, accent: c })}
                      className="w-9 h-9 rounded-lg transition"
                      style={{ background: c, border: sel ? '3px solid #fff' : '1px solid rgba(255,255,255,0.15)' }}
                      data-testid={`at-color-${c.replace('#','')}`} aria-label={c} />
                  );
                })}
              </div>
            </Field>
            <Field label="Status" full>
              <button type="button" onClick={() => setForm({ ...form, active: !form.active })}
                className="px-4 py-2 rounded-lg flex items-center gap-2 transition w-fit"
                style={{
                  background: form.active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1px solid ${form.active ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
                  color: form.active ? '#22FF88' : '#FCA5A5',
                }}
                data-testid="at-toggle-active">
                {form.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {form.active ? 'Active' : 'Inactive'}
              </button>
            </Field>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <Button variant="outline" onClick={cancelEdit} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="at-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Task'}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#131822]">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0E14] text-gray-400 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-3 py-3 text-left">Order</th>
              <th className="px-3 py-3 text-left">Icon</th>
              <th className="px-3 py-3 text-left">Title</th>
              <th className="px-3 py-3 text-left">Description</th>
              <th className="px-3 py-3 text-left">Reward</th>
              <th className="px-3 py-3 text-center">Active</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && tasks.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                No {activeTab} tasks yet. Click <span className="text-emerald-400">Seed Defaults</span> or <span className="text-emerald-400">New Task</span>.
              </td></tr>
            )}
            {tasks.map((t) => {
              const Icon = (ICON_OPTIONS.find((i) => i.name === t.icon) || ICON_OPTIONS[0]).Comp;
              return (
                <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`at-row-${t.id}`}>
                  <td className="px-3 py-3">{t.sort_order}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center"
                      style={{ background: `${t.accent}22`, border: `1px solid ${t.accent}55` }}>
                      <Icon className="w-4 h-4" style={{ color: t.accent }} />
                    </span>
                  </td>
                  <td className="px-3 py-3 font-bold">{t.title}</td>
                  <td className="px-3 py-3 text-gray-400 max-w-[260px] truncate">{t.description}</td>
                  <td className="px-3 py-3">
                    <span className="display font-extrabold text-xs px-2 py-0.5 rounded" style={{ background: t.accent, color: '#0B0F0C' }}>
                      {t.reward}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => toggleActive(t)} className="inline-flex items-center" data-testid={`at-toggle-${t.id}`}>
                      {t.active
                        ? <ToggleRight className="w-6 h-6 text-emerald-400" />
                        : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => startEdit(t)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`at-edit-${t.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(t)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`at-delete-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-xs uppercase tracking-wider text-gray-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold transition"
      style={{
        background: active ? '#22C55E' : 'transparent',
        color: active ? '#02140A' : 'rgba(255,255,255,0.7)',
      }}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
